/**
 * Swing `FormatAdapter.check` for TEXT / TEXTAREA insert filter and verify.
 * Display `format()` / getData `unformat()` are out of scope.
 */

const DATE_TIME_DEFAULT = 'dd.MM.yyyy HH:mm:ss'
const DATE_TIME_SHORT = 'dd.MM.yyyy HH:mm'
const DATE_PATTERN = 'dd.MM.yyyy'
const TIME_PATTERN = 'HH:mm'
const TIME_DATE_DEFAULT = 'HH:mm:ss dd.MM.yyyy'

const PASS_THROUGH_EXACT = new Set(['first', 'named'])

function isPassThroughNamed(formatName: string): boolean {
  const lower = formatName.toLowerCase()
  if (PASS_THROUGH_EXACT.has(lower)) return true
  if (lower.startsWith('specified')) return true
  if (lower.startsWith('subtableformat=')) return true
  if (lower.startsWith('subtableextformat=')) return true
  if (lower.startsWith('iconsubtableformat=')) return true
  if (lower.startsWith('scriptformat=')) return true
  if (lower.startsWith('extformat=')) return true
  if (lower.startsWith('subtablelistboxformat=')) return true
  if (lower.startsWith('filesize')) return true
  if (lower === 'frequencyformat') return true
  if (lower.startsWith('dividableformat=')) return true
  return false
}

function isLetterToken(ch: string): boolean {
  return (ch > 'a' && ch < 'z') || (ch > 'A' && ch < 'Z')
}

function javaSplitNonDigits(st: string): string[] {
  const parts = st.split(/[^0-9]/)
  while (parts.length > 0 && parts[parts.length - 1] === '') parts.pop()
  return parts
}

function parseNonNegativeInt(token: string): number | null {
  if (!token) return null
  const n = Number.parseInt(token, 10)
  if (!Number.isFinite(n)) return null
  return n
}

function checkIntegerDigits(value: string, emptyResult: string | null): string | null {
  if (!value) return emptyResult
  for (const ch of value) {
    if (ch < '0' || ch > '9') return null
  }
  return value
}

function checkDecimalNumber(value: string, emptyResult: string, maxFractionDigits?: number): string | null {
  if (!value) return emptyResult
  let foundPoint = false
  for (const ch of value) {
    if ((ch < '0' || ch > '9') && ch !== '.' && ch !== ',') return null
    if (ch === '.' || ch === ',') {
      if (foundPoint) return null
      foundPoint = true
    }
  }
  if (maxFractionDigits != null && foundPoint) {
    const dot = value.indexOf('.')
    if (dot >= 0 && value.substring(dot + 1).length > maxFractionDigits) return null
  }
  return value.replace(/,/g, '.')
}

function checkSpecifiedLengthDecimal(value: string, length: number): string | null {
  if (!value) {
    const zeros = '0'.repeat(Math.max(0, length - 2))
    return `0.${zeros}`
  }
  let foundPoint = false
  let i = 0
  for (const ch of value) {
    i += 1
    if ((ch < '0' || ch > '9') && ch !== '.' && ch !== ',' && ch !== '-') return null
    if (ch === '.' || ch === ',') {
      if (foundPoint) return null
      foundPoint = true
    }
    if ((i > length && !foundPoint) || (i > length + 1 && foundPoint)) return null
    if (i > 1 && ch === '-') return null
  }
  return value.replace(/,/g, '.')
}

function checkMoney(value: string): string | null {
  if (!value) return ''
  return /^-?[0-9 \u00A0]*([,.][0-9]{0,2}|)$/.test(value) ? value : null
}

function checkDateTimePattern(
  value: string,
  datePattern: string,
  kind: 'dateTime' | 'time' | 'timeDate',
): string | null {
  if (value == null) return null
  const st = String(value)
  if (st.length > datePattern.length) return null
  for (let i = 0; i < st.length; i++) {
    const formatChar = datePattern.charAt(i)
    const strChar = st.charAt(i)
    if (isLetterToken(formatChar)) {
      if (strChar < '0' || strChar > '9') return null
    } else if (formatChar !== strChar) {
      return null
    }
  }
  const split = javaSplitNonDigits(st)
  for (let i = 0; i < split.length; i++) {
    const s1 = split[i]
    if (!s1) continue
    const n = parseNonNegativeInt(s1)
    if (n == null) return null
    if (kind === 'dateTime') {
      if (i === 0 && n > 31) return null
      if (i === 1 && n > 12) return null
      if (i === 3 && n > 23) return null
      if (datePattern === DATE_TIME_DEFAULT || datePattern === DATE_TIME_SHORT) {
        if (i === 4 && n > 59) return null
        if (i === 5 && n > 59) return null
        if (i === 6 && n > 59) return null
      }
    } else if (kind === 'time') {
      if (i === 0 && n > 23) return null
      if (i === 1 && n > 59) return null
    } else if (kind === 'timeDate') {
      if (datePattern === TIME_DATE_DEFAULT) {
        if (i === 0 && n > 23) return null
        if (i === 1 && n > 59) return null
        if (i === 2 && n > 59) return null
        if (i === 3 && n > 31) return null
        if (i === 4 && n > 12) return null
      } else if (datePattern === TIME_PATTERN) {
        if (i === 0 && n > 23) return null
        if (i === 1 && n > 59) return null
      }
    }
  }
  return value
}

function compileJavaLikeRegexp(format: string): RegExp | null {
  let pattern = format
  let flags = ''
  const inlineFlags = pattern.match(/^\(\?([a-z-]+)\)/i)
  if (inlineFlags) {
    const f = inlineFlags[1]
    if (f.includes('i')) flags += 'i'
    if (f.includes('m')) flags += 'm'
    if (f.includes('s')) flags += 's'
    if (f.includes('u')) flags += 'u'
    pattern = pattern.slice(inlineFlags[0].length)
  }
  try {
    return new RegExp(`^(?:${pattern})$`, flags)
  } catch {
    return null
  }
}

function checkRegExp(value: string, format: string): string | null {
  if (!value) return ''
  const re = compileJavaLikeRegexp(format)
  if (!re) return value
  return re.test(value) ? value : null
}

function resolveDateTimeKind(formatName: string): { pattern: string; kind: 'dateTime' | 'time' | 'timeDate' } | null {
  if (formatName.toLowerCase() === 'datetime') {
    return { pattern: DATE_TIME_DEFAULT, kind: 'dateTime' }
  }
  if (formatName.toLowerCase() === 'date') {
    return { pattern: DATE_PATTERN, kind: 'dateTime' }
  }
  if (formatName === DATE_TIME_SHORT) {
    return { pattern: DATE_TIME_SHORT, kind: 'dateTime' }
  }
  if (formatName === DATE_PATTERN) {
    return { pattern: DATE_PATTERN, kind: 'dateTime' }
  }
  if (formatName === DATE_TIME_DEFAULT) {
    return { pattern: DATE_TIME_DEFAULT, kind: 'dateTime' }
  }
  if (formatName.toLowerCase() === 'timedate') {
    return { pattern: TIME_DATE_DEFAULT, kind: 'timeDate' }
  }
  if (formatName.toLowerCase() === 'time' || formatName === TIME_PATTERN) {
    return { pattern: TIME_PATTERN, kind: 'time' }
  }
  return null
}

/**
 * Swing `FormatAdapter.check`. Returns null when the string is not allowed.
 */
export function checkTextFieldFormat(formatName: string | null | undefined, value: unknown): string | null {
  const name = String(formatName ?? '').trim()
  const s = value == null ? '' : String(value)
  if (!name) return s

  if (isPassThroughNamed(name)) return s

  const lower = name.toLowerCase()
  if (lower === 'integerformat') return checkIntegerDigits(s, '0')
  if (lower === 'emptiableintegerformat') return checkIntegerDigits(s, null)
  if (lower === 'ravagedintegerformat') return checkIntegerDigits(s, '')
  if (lower === 'decimalformat') return checkDecimalNumber(s, '0.00')
  if (lower === 'decimal5format') return checkDecimalNumber(s, '0', 5)
  if (lower === 'tvcformat') {
    const digits = checkIntegerDigits(s, '0')
    return digits == null ? null : digits.replace(/,/g, '.')
  }
  if (lower.startsWith('decimalformatwithspecifiedlength')) {
    const rawLen = name.substring('DecimalFormatWithSpecifiedLength'.length)
    const length = Number.parseInt(rawLen, 10)
    if (!Number.isFinite(length)) return s
    return checkSpecifiedLengthDecimal(s, length)
  }
  if (lower.startsWith('moneyformat')) return checkMoney(s)

  const dateTime = resolveDateTimeKind(name)
  if (dateTime) return checkDateTimePattern(s, dateTime.pattern, dateTime.kind)

  return checkRegExp(s, name)
}

/** Empty is always allowed (Swing overrides insertString, not remove). */
export function isTextFieldFormatValueAccepted(
  formatName: string | null | undefined,
  value: unknown,
): boolean {
  const s = value == null ? '' : String(value)
  if (!s) return true
  return checkTextFieldFormat(formatName, s) != null
}

export function acceptTextFieldFormatInput(
  formatName: string | null | undefined,
  nextValue: unknown,
): boolean {
  return isTextFieldFormatValueAccepted(formatName, nextValue)
}

export function failsTextFieldFormatVerify(
  formatName: string | null | undefined,
  value: unknown,
): boolean {
  const name = String(formatName ?? '').trim()
  if (!name) return false
  return !isTextFieldFormatValueAccepted(name, value)
}
